import Image from 'next/image'
import Link from 'next/link'

import { atmLogo, closeIcon } from '@ui/assets'
import { useSignUpHref } from '@ui/layout/signUpHref'
import { marketingUrl } from '@ui/lib/urls'

export interface NavLink {
  label: string
  href: string
  isAnchor?: boolean
}

export interface HeaderProps {
  showBanner?: boolean
  onCloseBanner?: () => void
  navLinks?: NavLink[]
  showBackButton?: boolean
}

const DEFAULT_LINKS: NavLink[] = [
  { label: 'About', href: marketingUrl('/about') },
  { label: 'Access Cash', href: marketingUrl('/#access-cash') },
  { label: 'Earn Rewards', href: marketingUrl('/#earn-rewards') },
  { label: 'Comparisons', href: marketingUrl('/compare') },
]

export function Header({
  showBanner = true,
  onCloseBanner,
  navLinks,
  showBackButton = false,
}: HeaderProps) {
  const links = navLinks || DEFAULT_LINKS
  const signUpHref = useSignUpHref()

  return (
    <>
      {/* Top Banner */}
      {showBanner && (
        <div className="relative z-50 hidden bg-[#0a3754] px-4 py-3 text-center lg:block">
          <p className="text-sm text-white sm:text-base">
            Join millions of users and take control of your finances.{' '}
            <a
              href={marketingUrl('/download')}
              className="border-b border-white font-bold"
            >
              Download App
            </a>
          </p>
          <button
            onClick={onCloseBanner}
            className="absolute right-4 top-1/2 -translate-y-1/2"
            aria-label="Close banner"
          >
            <Image src={closeIcon} width={24} height={24} alt="Close" />
          </button>
        </div>
      )}

      {/* Custom Navigation */}
      <header className="sticky top-0 z-40 px-4 py-4 sm:px-6 lg:px-8">
        {/* Blur scrim with gradient mask - strongest at top, none at bottom */}
        <div
          className="pointer-events-none absolute inset-0 backdrop-blur-3xl"
          style={{
            maskImage:
              'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.8) 30%, rgba(0,0,0,0.4) 60%, transparent 100%)',
            WebkitMaskImage:
              'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.8) 30%, rgba(0,0,0,0.4) 60%, transparent 100%)',
          }}
        />

        {/* Centered navigation - centered relative to viewport */}
        <div className="pointer-events-none absolute inset-x-0 top-4 z-20 hidden justify-center lg:flex">
          <nav className="pointer-events-auto inline-flex items-center gap-4 rounded-[62px] bg-white/60 px-8 py-3 shadow-[inset_0_1px_1px_rgba(255,255,255,0.5)] backdrop-blur-sm">
            {links.map((link) =>
              link.isAnchor ? (
                <a
                  key={link.href}
                  href={link.href}
                  className="hover:text-blueberry-700 text-[17px] font-bold tracking-[-0.34px] text-[#0a2d4a]"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.href}
                  href={link.href}
                  className="hover:text-blueberry-700 text-[17px] font-bold tracking-[-0.34px] text-[#0a2d4a]"
                >
                  {link.label}
                </Link>
              ),
            )}
          </nav>
        </div>

        <div className="relative z-10 mx-auto flex max-w-7xl items-center justify-between">
          <Link href="/" className="flex items-center">
            <Image
              src={atmLogo}
              alt="ATM.com"
              className="h-[52px] w-auto"
              style={{
                filter: 'drop-shadow(0 2px 8px rgba(230, 247, 255, 0.8))',
              }}
            />
          </Link>

          <div className="flex items-center gap-2">
            {showBackButton ? (
              <Link
                href="/"
                className="rounded-full bg-[#29bfed] px-5 py-3 text-[17px] font-bold text-white transition-colors hover:bg-[#29bfed]/90"
              >
                Back to ATM.com
              </Link>
            ) : (
              <>
                <Link
                  href={signUpHref}
                  className="hidden rounded-full border-2 border-[#2cceff] bg-[#e6f7ff] px-5 py-[10px] text-[17px] font-bold text-[#0a2d4a] transition-colors hover:bg-[#2cceff]/10 sm:block"
                >
                  Get Started
                </Link>
                <Link
                  href={marketingUrl('/download')}
                  className="rounded-full bg-[#29bfed] px-5 py-3 text-[17px] font-bold text-white transition-colors hover:bg-[#29bfed]/90"
                >
                  Download App
                </Link>
              </>
            )}
          </div>
        </div>
      </header>
    </>
  )
}
